import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import toast from 'react-hot-toast';

import { gql, useQuery } from '@apollo/client';

import { logTapestryEvent } from 'logger';

import { AssetClass, FileState, ProductType } from '__generated__/globalTypes';
import { GetLoanTape, GetLoanTapeVariables } from './__generated__/TapeUploadFlow';
import { TapesUploadedTableFragments } from './components/TapesUploadedTable';
import type { InProgressFile } from './components/UploadHistory';
import MapRequiredFieldsCard, { MappingCardFragments } from './fieldMapping/MapRequiredFieldsCard';
import LoanTapeUploader from './loanTapeUploader/LoanTapeUploader';
import ProcessingReportCard, { ProcessingReportCardFragments } from './ProcessingReportCard';
import { PipelineStatus } from './tapestry/PipelineStatus';
import { RoleGatedButton } from './tapestry/RoleGatedButton';
import { TapestryMappingGate } from './tapestry/TapestryMappingGate';
import {
  TapestryOutcomePanel,
  TapestryOutcomePanelFragments,
  outcomePanelView,
} from './tapestry/TapestryOutcomePanel';
import { TapestryReviewCard, TapestryReviewCardFragments } from './tapestry/TapestryReviewCard';
import { tapestryErrorMessage } from './tapestry/errorMessages';
import {
  humanizeTapestryStage,
  isInProgressOutcome,
  isTapestryFile,
  isTerminalOutcome,
  isWaitingStage,
  tapestryStageToStep,
} from './tapestry/stages';
import { extractTapestryErrorCode, useTapestryJobActions } from './tapestry/useTapestryJobActions';
import { Card, CardHeading, CardInstructions } from './styles';
import TapeDetailHeader, { TapeDetailHeaderSkeleton } from './TapeDetailHeader';
import type { TapeUploadStep } from './TapeUploadStepper';
import { useTapestryJob } from './tapestry/useTapestryJob';

const GET_LOAN_TAPE_QUERY = gql`
  query GetLoanTape($tapeId: ID!) {
    user {
      id
      company {
        id
        loanTape: file(id: $tapeId) {
          id
          tapestry_enabled
          tapestry_stage
          ...TapesUploadedTableFile
          ...MappingCardFile
          ...ProcessingReportCardFile
          ...TapestryReviewCardFile
          ...TapestryOutcomePanelFile
        }
      }
    }
  }
  ${MappingCardFragments.loanTape}
  ${ProcessingReportCardFragments.loanTape}
  ${TapesUploadedTableFragments.loanTape}
  ${TapestryReviewCardFragments.loanTape}
  ${TapestryOutcomePanelFragments.loanTape}
`;

export const TapeUploadFlow: React.FC = () => {
  const tapeId = useParams<{ tapeId: string }>().tapeId;

  // Upload progress lives above the list/detail split: list and detail are the
  // same Route (`tape-upload/:tapeId?`), so this component survives navigating
  // between them while LoanTapeUploader unmounts. The S3 XHR keeps running
  // through that unmount (useS3Uploader has no abort-on-unmount) — keeping the
  // rows here means the progress UI tells the truth when the user comes back.
  const [uploadsInProgress, setUploadsInProgress] = useState<InProgressFile[]>([]);

  // Seed the optimistic row at drop (before the first byte) carrying the asset
  // class / product so the row in the table shows them immediately rather than
  // popping in when the server row lands. startedAt sorts it to the top.
  const handleUploadStart = useCallback(
    (file: File, assetClass: AssetClass | undefined, productType: ProductType) => {
      setUploadsInProgress((prev) => {
        if (prev.some((f) => f.name === file.name && f.lastModified === file.lastModified)) {
          return prev;
        }
        return [
          ...prev,
          {
            name: file.name,
            lastModified: file.lastModified,
            assetClass,
            productType,
            startedAt: new Date().toISOString(),
            progress: { percentage: 0 },
          },
        ];
      });
    },
    [],
  );

  // Drop the optimistic row when the upload fails — it never became a tape, and
  // the error is surfaced via toast. Without this the row would hang forever.
  const handleUploadError = useCallback((file: File) => {
    setUploadsInProgress((prev) =>
      prev.filter((f) => !(f.name === file.name && f.lastModified === file.lastModified)),
    );
  }, []);

  const handleUploadSuccess = useCallback((file: File, _versionId?: string, uuid?: string) => {
    setUploadsInProgress((prev) => {
      const existingFile = prev.find(
        (f) => f.name === file.name && f.lastModified === file.lastModified,
      );
      if (existingFile) {
        existingFile.id = uuid;
      }
      return [...prev];
    });
  }, []);

  const handleUploadProgress = useCallback((progress: number, file?: File) => {
    if (!file) {
      return;
    }
    setUploadsInProgress((prev) => {
      const existingFile = prev.find(
        (f) => f.name === file.name && f.lastModified === file.lastModified,
      );
      if (existingFile) {
        existingFile.progress = {
          percentage: progress,
        };
      } else {
        prev.push({
          name: file.name,
          lastModified: file.lastModified,
          progress: {
            percentage: progress,
          },
        });
      }
      return [...prev];
    });
  }, []);

  const { data, loading, error, refetch } = useQuery<GetLoanTape, GetLoanTapeVariables>(
    GET_LOAN_TAPE_QUERY,
    {
      variables: { tapeId: tapeId as string },
      skip: !tapeId,
      pollInterval: 5000,
      fetchPolicy: 'network-only',
    },
  );

  // On a tape→tape switch Apollo keeps the previous result in `data` until the
  // new request resolves, so guard on id: only treat the loaded tape as the
  // current one when it matches the route. Otherwise it's stale (the skeleton
  // shows) and we never flash the wrong filename/status/stepper. `loadedFile`
  // (id-agnostic) still drives the not-found check so a genuinely missing tape
  // is distinguished from an in-flight switch.
  const loadedFile = data?.user.company.loanTape;
  const selectedFile = loadedFile?.id === tapeId ? loadedFile : undefined;

  // A file is Tapestry-managed when the api reports `tapestry_enabled` on it
  // (ENG-2788, now live). For every legacy upload this is false, so the entire
  // Tapestry branch below stays inert and the legacy flow is unchanged; it only
  // turns on for tapes processed through the pipeline (flag-gated per company).
  const isTapestry = isTapestryFile(selectedFile);

  // Tapestry mode drives the UI off its own per-stage status endpoint, polled
  // while the pipeline is active. Skipped (dormant) for legacy uploads so
  // flag-off behavior is byte-for-byte unchanged.
  const {
    job: tapestryJob,
    completionOutcome,
    refetch: refetchTapestryJob,
  } = useTapestryJob(tapeId, { skip: !isTapestry });

  const { cancel: cancelTapestryJob, loading: cancelling } = useTapestryJobActions();
  // Latch the cancel request so it can't fire twice between the mutation
  // resolving and the refetch moving the run to a terminal outcome (which
  // unmounts the button). Self-heals if the refetch fails.
  const [cancelRequested, setCancelRequested] = useState(false);
  // Synchronous re-entry guard: `cancelRequested`/`cancelling` only disable the
  // button on the next render, so a double-click could fire two cancelTapestryJob
  // calls in the same tick. The ref blocks the second immediately.
  const cancelInFlight = useRef(false);
  // The cancel latch lives on this (unkeyed) page, so reset it when the route
  // switches to a different tape, and whenever the run reaches a terminal
  // outcome — so a stuck latch (e.g. cancel succeeded but the status refetch
  // failed) can't outlive the action it guarded.
  useEffect(() => {
    setCancelRequested(false);
    cancelInFlight.current = false;
  }, [tapeId, completionOutcome]);
  const onCancelTapestryJob = async () => {
    if (!selectedFile || cancelInFlight.current || cancelRequested) {
      return;
    }
    cancelInFlight.current = true;
    try {
      await cancelTapestryJob(selectedFile.id);
    } catch (error) {
      cancelInFlight.current = false;
      toast.error(tapestryErrorMessage(extractTapestryErrorCode(error)));
      return;
    }
    setCancelRequested(true);
    try {
      await refetchTapestryJob();
    } catch {
      setCancelRequested(false);
      cancelInFlight.current = false;
    }
  };

  // Terminal/recovery panel — routed entirely off completion_outcome (from the
  // tape-matched job, not GetLoanTape), so it still resolves if the File query is
  // failing. Gate on what the panel will actually render (outcomePanelView)
  // rather than isTerminalOutcome — the latter is true for unknown / unmodelled
  // outcomes the panel renders nothing for, which would mount an empty wrapper.
  const showOutcomePanel =
    isTapestry && !!selectedFile && outcomePanelView(completionOutcome) !== null;

  // GetLoanTape fails server-side when the tape's field_mapping holds a value
  // that isn't a valid LoanField (a backend mapper bug — ENG-2869): the invalid
  // value propagates up the non-null field_mapping → File and errors the whole
  // query. Apollo keeps the last-good data, so selectedFile freezes at an earlier
  // stage and the run would otherwise sit on the pipeline-status spinner forever.
  // Surface that as an explicit, retryable state (keyed on graphQLErrors — a
  // server-side data error — not a transient networkError, which the view
  // tolerates). The status/gates below are suppressed since they'd render frozen
  // or empty data; the outcome panel still wins (it reads the job, not the File).
  const tapeDetailsUnavailable = isTapestry && !!error?.graphQLErrors?.length && !showOutcomePanel;

  // Gate visibility is driven by the tape-matched job (useTapestryJob discards a
  // job whose file_id != this tape) — NOT File.tapestry_stage. The 5s File poll
  // lags the job and, on a corrupt field_mapping, freezes entirely; keying on it
  // would let a stalled File query hide a gate the run has actually reached (the
  // deadlock). The post-action job refetch hides the gate on success, the
  // resume-after-gate effect re-kicks a lost refetch, and !showOutcomePanel lets
  // a terminal outcome win so a stale WAITING_FOR_* can't mount Confirm/Reject
  // beside a success/rejected banner.
  const showMappingGate =
    isTapestry &&
    !!selectedFile &&
    tapestryJob?.current_stage === 'WAITING_FOR_MAPPING' &&
    !showOutcomePanel &&
    !tapeDetailsUnavailable;

  const showReviewGate =
    isTapestry &&
    !!selectedFile &&
    !!tapestryJob?.review?.ready_for_review &&
    !showOutcomePanel &&
    !tapeDetailsUnavailable;

  // A human gate (mapping or review) pauses the pipeline waiting on the user.
  const atHumanGate = showMappingGate || showReviewGate;

  // Live pipeline stepper, shown only while the run is actively processing with
  // no human gate up. At a gate the pipeline is paused on the user, so the gate
  // is the body's sole focus (the header stepper is the wayfinder) — a big
  // animated in-progress stepper there both misleads and shoves the gate below
  // the fold. Cancel stays reachable at the gate from the sticky header's
  // stepper row (passed to TapeDetailHeader as stepperAction).
  const showPipelineStatus =
    isTapestry &&
    !!tapestryJob &&
    isInProgressOutcome(completionOutcome) &&
    !tapeDetailsUnavailable &&
    !atHumanGate;

  // Resume-after-gate backstop. The job stops polling at a human gate and only
  // restarts on the one-shot refetch after the user's action. If that refetch is
  // lost (the gate cards fire-and-forget it), the job stays parked at
  // WAITING_FOR_* until a manual reload — so when the 5s File poll has advanced
  // past the gate but the job hasn't, re-kick the refetch. No-op while both agree
  // at the gate; self-terminates once a non-waiting stage resumes polling. Skip
  // it while GetLoanTape is erroring: selectedFile is then frozen (not genuinely
  // advanced), so the stage mismatch is spurious and re-kicking would just spin.
  useEffect(() => {
    if (
      isTapestry &&
      selectedFile &&
      tapestryJob &&
      !error &&
      isWaitingStage(tapestryJob.current_stage) &&
      selectedFile.tapestry_stage !== tapestryJob.current_stage
    ) {
      void refetchTapestryJob();
    }
  }, [isTapestry, selectedFile, tapestryJob, error, refetchTapestryJob]);

  // Emit one TapestryAction "outcome" event when a run reaches a terminal
  // completion_outcome (the success / no-persist / failure split that drives the
  // pipeline's product metrics). Keyed per (file, outcome) so the 5s poll can't
  // re-fire it, and so switching tapes logs each one once.
  const loggedOutcomeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isTapestry || !selectedFile || !isTerminalOutcome(completionOutcome)) {
      return;
    }
    const key = `${selectedFile.id}:${completionOutcome}`;
    if (loggedOutcomeRef.current === key) {
      return;
    }
    loggedOutcomeRef.current = key;
    logTapestryEvent('outcome', { fileId: selectedFile.id, completionOutcome });
  }, [isTapestry, selectedFile, completionOutcome]);

  const mapFieldsCardRef = useRef<HTMLDivElement>(null);
  const scrollToMapFieldsCard = () => {
    mapFieldsCardRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const processingReportCardRef = useRef<HTMLDivElement>(null);
  // Tapestry mode renders one body (whichever surface is active) instead of the
  // legacy map/report cards, so step clicks scroll here rather than to refs on
  // suppressed cards.
  const tapestryBodyRef = useRef<HTMLDivElement>(null);

  const allowedStates = [
    FileState.PROCESSED,
    FileState.READY_TO_PERSIST,
    FileState.PERSISTING,
    FileState.PERSISTED,
    FileState.ERROR_HEADER,
    FileState.ERROR_PASSWORD,
    FileState.ERROR,
  ];

  // Legacy cards are gated on FileState / parsed headers and call legacy
  // actions (submitFileHeaderMap, ingestFile). They are suppressed in Tapestry
  // mode because those actions don't apply to the pipeline — the Tapestry
  // equivalents are PipelineStatus (ENG-2791), the mapping gate (ENG-2792), the
  // review gate (ENG-2793) and the terminal/recovery panel (ENG-2794).
  const shouldShowMappingCard = !isTapestry && selectedFile?.headers && !loading;

  const shouldShowProcessingReportCard =
    !isTapestry &&
    selectedFile &&
    selectedFile.processingReport &&
    !loading &&
    allowedStates.includes(selectedFile.state);

  // Single source of truth for the displayed Tapestry stage, shared by the
  // stepper and the header status pill. Prefer the polled job's current_stage
  // (it advances immediately on the post-gate refetch) over File.tapestry_stage
  // (the 5s GetLoanTape poll), so the header never lags or disagrees with the
  // PipelineStatus body; fall back to the file stage before the job has loaded.
  const liveTapestryStage = isTapestry
    ? (tapestryJob?.current_stage ?? selectedFile?.tapestry_stage ?? null)
    : null;

  // The stepper tracks the furthest card the selected tape has reached. Legacy
  // tapes derive it from the visible card; Tapestry tapes derive it from the
  // live pipeline stage (tapestryStageToStep) since they suppress both legacy cards.
  const currentStep: TapeUploadStep = isTapestry
    ? tapestryStageToStep(liveTapestryStage)
    : shouldShowProcessingReportCard
      ? 3
      : shouldShowMappingCard
        ? 2
        : 1;

  const navigate = useNavigate();
  const handleStepClick = (step: TapeUploadStep) => {
    // Step 1 ("Upload a tape") lives on the list page, regardless of mode.
    if (step === 1) {
      navigate('/portfolio/tape-upload');
      return;
    }
    // Tapestry mode suppresses the legacy cards, so steps 2/3 scroll to the
    // single pipeline body rather than to refs on cards that aren't rendered.
    if (isTapestry) {
      tapestryBodyRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (step === 2) scrollToMapFieldsCard();
    if (step === 3) processingReportCardRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Single Cancel control, reused by the live stepper card (active processing)
  // and the header stepper row (at a human gate) — only one ever mounts at a
  // time (atHumanGate gates showPipelineStatus off), so the testid stays unique.
  const cancelProcessingButton = (
    <RoleGatedButton
      label="Cancel processing"
      type="secondary"
      data-testid="tapestry-cancel-button"
      disabled={cancelling || cancelRequested}
      onClick={() => void onCancelTapestryJob()}
    >
      Cancel processing
    </RoleGatedButton>
  );

  // Master-detail split (ENG-2845 follow-up): without a tape the route is the
  // list page (upload + history); with one it's that tape's detail page —
  // sticky identity/wayfinding header framing the mapping + report cards. The
  // list never renders here, so the long-page context loss is structural-fixed.
  if (!tapeId) {
    return (
      <LoanTapeUploader
        uploadsInProgress={uploadsInProgress}
        onUploadStart={handleUploadStart}
        onUploadSuccess={handleUploadSuccess}
        onUploadProgress={handleUploadProgress}
        onUploadError={handleUploadError}
      />
    );
  }

  // Distinguish a load failure from a deletion. notFound keys on loadedFile so a
  // mid-switch (previous tape still in `data`) shows the skeleton, not "missing".
  // failedToLoad keys on selectedFile (id-matched), NOT loadedFile: on a failed
  // tape→tape switch Apollo keeps the old tape in `data`, so loadedFile is truthy
  // while the new one never loads — keying on it would hang the skeleton with no
  // retry. A poll error on an already-loaded tape keeps selectedFile set, so the
  // view stays put instead of flashing an error.
  const failedToLoad = !loading && error != null && !selectedFile;
  const notFound = !loading && !error && !loadedFile;
  if (failedToLoad || notFound) {
    return (
      <div className="p-8 text-white">
        <p className="mb-4">
          {failedToLoad
            ? "Couldn't load this tape — the request failed. Check your connection and try again."
            : 'This tape no longer exists — it may have been removed.'}
        </p>
        <div className="flex gap-4">
          {failedToLoad && (
            <button
              type="button"
              className="text-pink-400 underline"
              onClick={() => {
                refetch().catch(() => {});
              }}
            >
              Retry
            </button>
          )}
          <button
            type="button"
            className="text-pink-400 underline"
            onClick={() => navigate('/portfolio/tape-upload')}
          >
            Back to tapes
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {selectedFile ? (
        <TapeDetailHeader
          tape={selectedFile}
          currentStep={currentStep}
          onStepClick={handleStepClick}
          tapestryStage={liveTapestryStage}
          // At a human gate the full pipeline-status card (which carries its own
          // Cancel) is suppressed, so surface Cancel here in the sticky header
          // rather than as a lone row above the gate.
          stepperAction={atHumanGate ? cancelProcessingButton : undefined}
        />
      ) : (
        <TapeDetailHeaderSkeleton />
      )}
      {/* Tapestry detail body: the live pipeline stepper (+ Cancel), the two
          human gates, and the terminal/recovery panel replace the legacy cards
          (suppressed in Tapestry mode). The placeholder is the honest fallback
          while nothing actionable is up yet. */}
      {isTapestry && selectedFile && (
        <div ref={tapestryBodyRef}>
          {tapeDetailsUnavailable && (
            <Card data-testid="tapestry-details-error">
              <CardHeading>Couldn’t load this tape</CardHeading>
              <CardInstructions>
                We couldn’t load this tape’s latest details — its column mapping data may be
                invalid. Retry, or head back and start a fresh upload.
              </CardInstructions>
              <div className="mt-3 flex gap-4">
                <button
                  type="button"
                  className="text-pink-400 underline"
                  onClick={() => {
                    refetch().catch(() => {});
                  }}
                >
                  Retry
                </button>
                <button
                  type="button"
                  className="text-pink-400 underline"
                  onClick={() => navigate('/portfolio/tape-upload')}
                >
                  Back to tapes
                </button>
              </div>
            </Card>
          )}
          {showPipelineStatus && tapestryJob && (
            <div className="relative flex flex-col items-end gap-2">
              <PipelineStatus job={tapestryJob} />
              {cancelProcessingButton}
            </div>
          )}
          {showMappingGate && (
            <div className="relative">
              {/* key by file id so switching tapes remounts the gate with a
                  fresh fieldsMap instead of carrying the previous tape's. */}
              <TapestryMappingGate
                key={selectedFile.id}
                selectedFile={selectedFile}
                onSubmitted={refetchTapestryJob}
              />
            </div>
          )}
          {showReviewGate && (
            <div className="relative">
              {/* key by file id so switching tapes remounts the card with a
                  fresh resolved-latch instead of carrying the previous tape's. */}
              <TapestryReviewCard
                key={selectedFile.id}
                selectedFile={selectedFile}
                onResolved={refetchTapestryJob}
              />
            </div>
          )}
          {showOutcomePanel && (
            <div className="relative mt-4">
              <TapestryOutcomePanel
                key={selectedFile.id}
                outcome={completionOutcome}
                completionMessage={tapestryJob?.completion_message}
                selectedFile={selectedFile}
                onRetried={refetchTapestryJob}
              />
            </div>
          )}
          {!tapeDetailsUnavailable &&
            !showPipelineStatus &&
            !showMappingGate &&
            !showReviewGate &&
            !showOutcomePanel && (
              <Card data-testid="tapestry-pipeline-placeholder">
                <CardHeading>Tapestry pipeline</CardHeading>
                <CardInstructions>
                  This tape is processed by the Tapestry pipeline
                  {selectedFile.tapestry_stage
                    ? ` — current stage: ${humanizeTapestryStage(selectedFile.tapestry_stage)}`
                    : ''}
                  .
                </CardInstructions>
              </Card>
            )}
        </div>
      )}
      <div ref={mapFieldsCardRef} className="relative">
        {shouldShowMappingCard && <MapRequiredFieldsCard selectedFile={selectedFile} />}
      </div>
      <div ref={processingReportCardRef}>
        {shouldShowProcessingReportCard && <ProcessingReportCard selectedFile={selectedFile} />}
      </div>
    </>
  );
};
